import { yupResolver } from "@hookform/resolvers/yup";
import { PenIcon } from "@src/assets/general-icons/PenIcon";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { Input } from "@src/components/input/Input";
import { purchasingActions } from "@src/store";
import { useAppDispatch } from "@src/store/hooks";
import { PuchaseInfoType, PurchaseInfoValidation } from "@src/validation/PurchaseInfoValidation";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { IAdvancedTab } from "../../AdvancedTab";
import { SubHeader } from "../SubHeader/SubHeader";
import styles from "./PurchaseInformation.module.scss";
import useHighlightedSection from "@src/hooks/useHighlightedSection";
import { HighlightedSectionEnum } from "@src/store/pricing/selectionSlice";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";

export const PurchaseInformation: React.FC<IAdvancedTab> = ({ inventoryItem, onSuccess }) => {
	const [canEdit, setCanEdit] = useState(false);
	const dispatch = useAppDispatch();

	const defaultValues = {
		Purchaser_Name: inventoryItem?.Purchaser_Name,
		Vendor: inventoryItem?.Vendor,
		Account_Name: inventoryItem?.Account_Name,
		Credit_Card: inventoryItem?.Credit_Card,
	};

	const {
		handleSubmit,
		control,
		reset,
		formState: { isSubmitting },
		watch,
	} = useForm({
		resolver: yupResolver(PurchaseInfoValidation),
		mode: "onChange",
		defaultValues,
	});

	const toggleEdit = () => {
		setCanEdit((prev) => !prev);
		mixpanelTrack(MixpanelCategory.ADD_EDIT_PURCHASE_INFORMATION);
	};

	useEffect(() => {
		reset(defaultValues);
		setCanEdit(false);
	}, [inventoryItem]);

	const onSubmit: SubmitHandler<PuchaseInfoType> = async (data) => {
		if (!inventoryItem?.PO_ID) {
			toast.warning("Purchase order ID missing");
			return;
		}
		const { Account_Name, Credit_Card, Purchaser_Name, Vendor } = data;
		const dataToSend = {
			Account_Name,
			Credit_Card,
			Purchaser_Name,
			Vendor,
			PO_ID: String(inventoryItem?.PO_ID),
		};
		const response = await dispatch(purchasingActions.updatePurchaseInfo(dataToSend));
		if (response.meta.requestStatus === "fulfilled") {
			toast.success("Purchase Information updated successfully");
			onSuccess?.();
			// Mixpanel purchase info saved here if needed
		}
		toggleEdit();
	};

	let rightSideContent = (
		<div aria-hidden onClick={toggleEdit} className={styles.content}>
			<PenIcon /> Edit
		</div>
	);

	const purchaserName = watch("Purchaser_Name") || "No info";
	const vendor = watch("Vendor") || "No info";
	const accountName = watch("Account_Name") || "No info";
	const creditCard = watch("Credit_Card") || "No info";

	if (canEdit)
		rightSideContent = (
			<div className={styles.content}>
				<Button
					type="button"
					onClick={toggleEdit}
					variant={ButtonVariant.Secondary}
					style={{ minWidth: "72px" }}
					controlSize="sm">
					Discard
				</Button>
				<Button
					type="submit"
					disabled={isSubmitting}
					isLoading={isSubmitting}
					variant={ButtonVariant.Primary}
					style={{ minWidth: "72px" }}
					controlSize="sm">
					Save
				</Button>
			</div>
		);

	const isHighlighted = useHighlightedSection(HighlightedSectionEnum.PurchaseInformation);

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.purchaseInfoContainer}>
			<SubHeader
				isHighlighted={isHighlighted}
				style={{
					height: "24px",
					alignItems: "flex-start",
				}}
				title="Purchase Information"
				tooltipText="Note: This applies to ALL listings on the selected PO"
				rightContent={rightSideContent}
			/>
			{canEdit ? (
				<div className={styles.inputs}>
					<Input controlSize="sm" label="Purchaser Name:" name="Purchaser_Name" control={control} />
					<Input controlSize="sm" label="Vendor:" name="Vendor" control={control} />
					<Input controlSize="sm" label="Account Name:" name="Account_Name" control={control} />
					<Input controlSize="sm" label="Credit Card:" name="Credit_Card" control={control} />
				</div>
			) : (
				<div className={styles.rawInfo}>
					<div>
						<span>Purchaser Name:</span> {purchaserName}
					</div>
					<div>
						<span>Vendor:</span> {vendor}
					</div>
					<div>
						<span>Account Name:</span> {accountName}
					</div>
					<div>
						<span>Credit Card:</span> {creditCard}
					</div>
				</div>
			)}
		</form>
	);
};
